import { LocalizedNameDto } from '@/api/Common/Types/NameDto'

import { TestQuestionWidgetType } from './TestQuestionDto'

export interface AppTestBuilderDto {
  testCategoryId: number
  testSubcategoryId: number
  tickmillCompanyId: number
  name: string
  tickmillProductId: number
  testSections: AppTestSectionDto[]
  maxFailScore: number
  maxMarginallyScore: number
}

export interface AppTestDetailsDto {
  category: string
  id: string
  maxFailScore: number
  maxReferredScore: number
  name: string
  sections: AppTestSectionDto[]
  shouldCalculateScore: boolean
  status: LocalizedNameDto
  subcategory: string
  tickmillCompanyId: number
  version: number
}

export interface AppTestSectionDto {
  id: string
  name: string
  description: string
  order: number
  questions: AppTestSectionQuestionDto[]
}

export interface AppTestSectionQuestionDto {
  id: string
  order: number
  isMandatory: boolean
  widgetOptions: WidgetOptions
  isProportional: boolean
  question: AppTestQuestionDto
  answers: AppTestAnswerDto[]
}

export interface AppTestAnswerDto {
  id: string
  order: number
  score: number
  productGovernanceRequired: boolean
  name: string
  code: string
  answerId: string
  productGovernanceDescription: string | null
  answer: AppTestQuestionsAnswerDto
  onSelectMessage: TestBuilderAnswerMessage | null
}

export interface AppTestQuestionDto {
  id: string
  text: string
  description: string
  errorMessage: string
  widgetName: string
  widgetType: TestQuestionWidgetType
  tooltip: string
  infoCard: string
  code: string
}

export interface TestBuilderAnswerMessage {
  typeId: number
  message: string
}

export interface AppTestQuestionsAnswerDto {
  id: string
  text: string
  code: string
}

export type WidgetOnChange = {
  name: string
  values: string[]
}

export type WidgetUpload = {
  values: string[]
}

export type WidgetOptions = {
  regExp: string
  hide: boolean
  optional: boolean
  depend: string | string[]
  isMandatory: boolean
  onChange: WidgetOnChange[]
  upload: WidgetUpload
  info: string
  answerGroups?: WidgetAnswerGroup[] | null
}

export type CreateAppTestDto = Omit<AppTestBuilderDto, 'testSections'> & {
  testSections: Array<
    Omit<AppTestSectionDto, 'questions'> & {
      questions: Array<
        Omit<AppTestSectionQuestionDto, 'question' | 'answers'> & {
          question: AppTestQuestionDto & { answers: AppTestAnswerDto[] }
        }
      >
    }
  >
}

export type WidgetAnswerGroup = {
  id: string
  name: string
  values: string[]
}

export function moveAnswersIntoQuestion(dto: AppTestBuilderDto): CreateAppTestDto {
  return {
    ...dto,
    testSections: dto.testSections.map((section) => ({
      ...section,
      questions: section.questions.map((q) => {
        const { answers, question, ...rest } = q

        return {
          ...rest,
          question: {
            ...question,
            answers: answers ?? [],
          },
        }
      }),
    })),
  }
}

export const TestType = {
  appropriateness: 'appropriateness',
  economicProfile: 'economic-profile',
} as const

export type TestType = (typeof TestType)[keyof typeof TestType]
